import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { VerticalSteppedScroller } from "../stepped-scroller/vertical-stepped-scroller";
import { cn } from "~/util/cn";
import { Month, useMonths } from "~/util/hooks";

type DatePickerMonthYearProps = {
  initialMonth: Month;
  initialYear: number;
  yearStart: number;
  yearEnd: number;
  onChange: (payload: { year: number; month: Month }) => void;
};

export type DatePickerMonthYearRef = {
  reset: () => void;
};

export const DatePickerMonthYearScroll = forwardRef<
  DatePickerMonthYearRef,
  DatePickerMonthYearProps
>(({ initialMonth, initialYear, yearStart, yearEnd, onChange }, ref) => {
  const allMonths = useMonths();
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [selectedYear, setSelectedYear] = useState(initialYear);

  const years = useMemo(
    () =>
      Array.from({ length: yearEnd - yearStart + 1 }, (_, i) => yearEnd - i),
    [yearStart, yearEnd],
  );

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const filteredMonths = useMemo(() => {
    if (selectedYear === currentYear) {
      return allMonths.filter((month) => month.number <= currentMonth + 1);
    }
    return allMonths;
  }, [selectedYear, currentYear, currentMonth, allMonths]);

  useEffect(
    () => onChange({ year: selectedYear, month: selectedMonth }),
    [selectedMonth, selectedYear, onChange],
  );

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        setSelectedMonth(initialMonth);
        setSelectedYear(initialYear);
      },
    }),
    [initialMonth, initialYear, setSelectedMonth, setSelectedYear],
  );

  return (
    <div className="grid grid-cols-2 gap-1">
      <VerticalSteppedScroller
        options={filteredMonths}
        onChange={setSelectedMonth}
        itemsShown={5}
        itemHeight={48}
        gap={4}
        className="px-2 sm:px-4"
      >
        {(month, isSelected) => (
          <button
            type="button"
            className={cn(
              "btn-block btn",
              isSelected ? "btn-primary" : "btn-ghost",
            )}
          >
            {month.name}
          </button>
        )}
      </VerticalSteppedScroller>
      <VerticalSteppedScroller
        options={years}
        onChange={setSelectedYear}
        itemsShown={5}
        itemHeight={48}
        gap={4}
        className="px-2 sm:px-4"
      >
        {(year, isSelected) => (
          <button
            type="button"
            className={cn(
              "btn-block btn",
              isSelected ? "btn-primary" : "btn-ghost",
            )}
          >
            {year}
          </button>
        )}
      </VerticalSteppedScroller>
    </div>
  );
});
DatePickerMonthYearScroll.displayName = "DatePickerMonthYear";
